/*
 * Structural types rather than imports from api.ts: this module stays free of React (as
 * navigation.ts is) so it can be tested without a DOM. api.ts's Connector and Overview
 * satisfy these.
 */
type ConnectorStatus =
  | 'NOT_CONNECTED' | 'PENDING_VERIFICATION' | 'CONNECTED'
  | 'REAUTH_REQUIRED' | 'ERROR' | 'DISCONNECTED' | 'HISTORICAL_RETAINED'
  | 'NOT_CONFIGURED' | 'COMING_SOON';
interface Connector { readonly kind: string; readonly status: ConnectorStatus }
interface Overview {
  readonly contactsKnown: number;
  readonly crmOperations: number;
  readonly crm?: { readonly succeeded: number; readonly failed: number; readonly pending: number; readonly contactsCreatedLast7Days: number };
}

/**
 * Where a workspace stands, derived — never stored.
 *
 * Onboarding is resumable because there is nothing to resume: every step's state is read
 * from what the server already knows (a verified Keap key, a stored Anthropic key, a
 * connected mailbox, a capture, a CRM result). Leave halfway, come back on another device,
 * and the checklist is exactly where the work is. Free of React so it can be tested alone.
 */

export type SourceKey = 'card' | 'gmail' | 'outlook' | 'csv';

export interface SourceState {
  readonly key: SourceKey;
  /** One short phrase for the badge. */
  readonly status: string;
  readonly tone: 'ready' | 'action' | 'idle';
  /** What it needs before it works, in plain words; null when ready. */
  readonly needs: string | null;
}

export interface SetupInputs {
  readonly connectors: readonly Connector[] | undefined;
  readonly overview: Overview | undefined;
  /** Review's Needs review count, across every source. */
  readonly attention: number | undefined;
  /** Everything Review holds, any state — evidence that something was ever captured. */
  readonly reviewTotal: number | undefined;
  /** Active Zapier or webhook destinations. Undefined while loading or on an older server. */
  readonly activeDestinations?: number;
}

const statusOf = (connectors: readonly Connector[] | undefined, kind: string): ConnectorStatus | null =>
  connectors?.find((c) => c.kind === kind)?.status ?? null;

const isUsable = (s: ConnectorStatus | null) => s === 'CONNECTED' || s === 'PENDING_VERIFICATION';

export function keapReady(connectors: readonly Connector[] | undefined): boolean {
  return statusOf(connectors, 'keap') === 'CONNECTED';
}
export function aiKeyReady(connectors: readonly Connector[] | undefined): boolean {
  return isUsable(statusOf(connectors, 'anthropic'));
}
export function mailboxConnected(connectors: readonly Connector[] | undefined, kind: 'gmail' | 'microsoft'): boolean {
  return statusOf(connectors, kind) === 'CONNECTED';
}

/** The four ways in, each with what it needs. Unknown (still loading) reads as idle, never as ready. */
export function sourceStates(connectors: readonly Connector[] | undefined): Record<SourceKey, SourceState> {
  const ai = aiKeyReady(connectors);
  const mailbox = (kind: 'gmail' | 'microsoft', key: SourceKey, name: string): SourceState => {
    const s = statusOf(connectors, kind);
    if (s === 'CONNECTED') {
      return ai
        ? { key, status: 'Connected', tone: 'ready', needs: null }
        : { key, status: 'Connected', tone: 'action', needs: 'Add your Anthropic key so marked emails can be read.' };
    }
    if (s === 'REAUTH_REQUIRED' || s === 'ERROR') {
      return { key, status: 'Reconnect needed', tone: 'action', needs: 'Reconnect it in Settings → Connections.' };
    }
    if (s === 'NOT_CONFIGURED' || s === 'COMING_SOON') {
      return { key, status: 'Unavailable', tone: 'idle', needs: `${name} is not available in this workspace.` };
    }
    return { key, status: 'Not connected', tone: 'idle', needs: `Connect ${name} to capture from marked emails.` };
  };
  return {
    card: ai
      ? { key: 'card', status: 'Ready', tone: 'ready', needs: null }
      : { key: 'card', status: 'Needs AI key', tone: 'action', needs: 'Add your Anthropic key to read business cards.' },
    gmail: mailbox('gmail', 'gmail', 'Gmail'),
    outlook: mailbox('microsoft', 'outlook', 'Outlook'),
    csv: { key: 'csv', status: 'Ready', tone: 'ready', needs: null },
  };
}

export type StepId = 'account' | 'crm' | 'destinations' | 'ai' | 'capture' | 'review' | 'result';

export interface SetupStep {
  readonly id: StepId;
  readonly title: string;
  readonly detail: string;
  readonly done: boolean;
  /** Optional steps never hold the checklist open. */
  readonly optional: boolean;
}

/**
 * The guided path, in the order a new workspace meets it. A step is done when the server
 * shows its effect — not when somebody clicked past it.
 *
 * `capture`: something was captured (Review holds or awaits anything, or contacts are known).
 * `review`: a person approved something and it reached the CRM ledger (any CRM operation).
 * `result`: Keap confirmed at least one change (succeeded or reconciled).
 */
export function setupSteps(i: SetupInputs): SetupStep[] {
  const crmOps = i.overview?.crmOperations ?? 0;
  const succeeded = i.overview?.crm?.succeeded ?? 0;
  /*
   * Review's old task `total` does not count import rows, so a workspace with 21 imported
   * contacts waiting showed "capture your first contact" as not done. Anything waiting on a
   * person (`attention`) is a capture too.
   */
  const captured = (i.reviewTotal ?? 0) > 0 || (i.attention ?? 0) > 0
    || (i.overview?.contactsKnown ?? 0) > 0 || crmOps > 0;
  return [
    { id: 'account', title: 'Create your account and workspace', detail: 'You are signed in to your workspace.', done: true, optional: false },
    {
      id: 'crm', title: 'Connect Keap',
      detail: 'Your CRM is where approved contacts go. ContactPilot checks the key with a read-only call.',
      done: keapReady(i.connectors), optional: false,
    },
    {
      id: 'destinations', title: 'Send to Zapier, Make or n8n',
      detail: 'Optional. Approved contacts can also go to your own automations, with your pipeline and stage. Keap is still where they are approved against.',
      done: (i.activeDestinations ?? 0) > 0, optional: true,
    },
    {
      id: 'ai', title: 'Add your Anthropic API key',
      detail: 'Needed to read business cards and marked emails. Anthropic bills you directly. Not needed for CSV imports.',
      done: aiKeyReady(i.connectors), optional: true,
    },
    {
      id: 'capture', title: 'Capture your first contact',
      detail: 'Photograph a business card, mark an email, or upload a CSV file.',
      done: captured, optional: false,
    },
    {
      id: 'review', title: 'Review and approve',
      detail: 'Check the details, choose Contact Type and tags, then approve. Nothing reaches Keap before you do.',
      done: crmOps > 0, optional: false,
    },
    {
      id: 'result', title: 'See it in Keap',
      detail: 'ContactPilot shows a contact as sent only after Keap confirms it.',
      done: succeeded > 0, optional: false,
    },
  ];
}

/** Whether the checklist still has a required step open. */
export function setupComplete(steps: readonly SetupStep[]): boolean {
  return steps.every((s) => s.done || s.optional);
}

/** Connections ready, out of the three that make the product work end to end. */
export function connectionsReady(connectors: readonly Connector[] | undefined): { ready: number; of: number } {
  const mailbox = mailboxConnected(connectors, 'gmail') || mailboxConnected(connectors, 'microsoft');
  return { ready: [keapReady(connectors), aiKeyReady(connectors), mailbox].filter(Boolean).length, of: 3 };
}

/** "2 min ago", "3 h ago", "Oct 29" — relative while recent, a date after a week. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const s = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86_400) return `${Math.floor(s / 86_400)} d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** A greeting by the reader's own clock. */
export function greeting(now: Date = new Date()): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}
